import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Numeric } from '@blade/ui-kit'
import { walletStatsAtom } from '@root/entities/wallet/atoms'
import { useTranslation } from '@root/shared/localization'
import { Skeleton } from '@root/shared/ui-kit'

import type BigNumber from 'bignumber.js'

type Props = {}

export const WalletStatsShort: React.FC<Props> = () => {
  const [state] = useAtom(walletStatsAtom)
  const { t } = useTranslation()

  const renderValue = (
    state: AsyncStateN.AsyncStateN<unknown, BigNumber | undefined>,
  ) => {
    if (AsyncStateN.isSuccess(state)) {
      if (state.value === undefined) {
        return '-'
      }
      return (
        <Numeric
          value={state.value}
          preset="currency-amount-short"
          currency="$"
        />
      )
    }
    return <Skeleton width={30} height={15} paddingBlock={2} />
  }

  const dailyVolumeUsdState = useMemo(() => {
    return pipe(
      state,
      AsyncStateN.map((it) => it?.dailyVolumeUsd),
    )
  }, [state])

  const topProfitUsdState = useMemo(() => {
    return pipe(
      state,
      AsyncStateN.map((it) => it?.topProfit),
    )
  }, [state])

  const summaryUsdState = useMemo(() => {
    return pipe(
      state,
      AsyncStateN.map((it) => it?.summaryUsd),
    )
  }, [state])

  return (
    <Root>
      <Group>
        <Title>{t('wallet.stats.daily_flow')}</Title>
        <Value>{renderValue(dailyVolumeUsdState)}</Value>
      </Group>
      <Separator />
      <Group>
        <Title>{t('wallet.stats.top_profit')}</Title>
        <Value>{renderValue(topProfitUsdState)}</Value>
      </Group>
      <Separator />
      <Group>
        <Title>{t('wallet.stats.summary')}</Title>
        <Value>{renderValue(summaryUsdState)}</Value>
      </Group>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  gap: 8px;
  align-content: center;
  align-items: center;
`

const Group = styled.div`
  display: grid;
  gap: 3px;
  align-items: center;
  align-content: center;
  min-width: 64px;
`

const Title = styled.div`
  color: var(--text-9);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const Value = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
`

const Separator = styled.div`
  background: var(--background-5);

  width: 1px;
  height: 100%;
  border-radius: 2px;
`
